import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { Checkbox } from 'components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { teamsOutlineCardButtonClassName } from './teams-styles';

export type TeamsPeopleColumnId = 'creditAccess' | 'lastLogin' | 'joinedOn' | 'status';

export type TeamsPeopleColumnVisibility = Record<TeamsPeopleColumnId, boolean>;

export const TEAMS_PEOPLE_COLUMN_DEFAULTS: TeamsPeopleColumnVisibility = {
  creditAccess: true,
  lastLogin: true,
  joinedOn: false,
  status: true,
};

const COLUMN_OPTIONS: { id: TeamsPeopleColumnId; labelKey: string; defaultValue: string }[] = [
  { id: 'creditAccess', labelKey: 'TEAMS_COL_CREDIT_ACCESS', defaultValue: 'Credit access' },
  { id: 'lastLogin', labelKey: 'TEAMS_COL_LAST_LOGIN', defaultValue: 'Last login' },
  { id: 'joinedOn', labelKey: 'TEAMS_COL_JOINED_ON', defaultValue: 'Joined on' },
  { id: 'status', labelKey: 'TEAMS_COL_STATUS', defaultValue: 'Status' },
];

type TeamsViewColumnsPopoverProps = {
  visibility: TeamsPeopleColumnVisibility;
  onVisibilityChange: (next: TeamsPeopleColumnVisibility) => void;
};

export function TeamsViewColumnsPopover({
  visibility,
  onVisibilityChange,
}: TeamsViewColumnsPopoverProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const titleColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-600';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(teamsOutlineCardButtonClassName, valueColor, borderColor, 'self-start')}
        >
          {t('TEAMS_VIEW', { defaultValue: 'View' })}
        </button>
      </PopoverTrigger>
      {/* Figma 10631:7912 — Columns visibility */}
      <PopoverContent
        align="end"
        sideOffset={8}
        className={cn(
          'w-48 rounded-xl border border-grey-100 bg-true-white p-1.5 pt-3 shadow-choice-card',
          'dark:border-border dark:bg-card'
        )}
      >
        <p className={cn('px-2.5 pb-1 text-xs font-medium leading-heading', titleColor)}>
          {t('TEAMS_COLUMNS', { defaultValue: 'Columns' })}
        </p>
        <ul className="flex flex-col">
          {COLUMN_OPTIONS.map((option) => {
            const checked = visibility[option.id];
            return (
              <li key={option.id}>
                <label
                  className={cn(
                    'flex h-10 cursor-pointer items-center gap-2.5 rounded-field px-2.5',
                    'hover:bg-grey-50 dark:hover:bg-muted'
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(next) =>
                      onVisibilityChange({ ...visibility, [option.id]: next === true })
                    }
                    className={cn(
                      'size-4 rounded-xs border-grey-400 shadow-none',
                      'data-[state=checked]:border-grey-900 data-[state=checked]:bg-grey-900',
                      'data-[state=checked]:text-true-white'
                    )}
                    aria-label={t(option.labelKey, { defaultValue: option.defaultValue })}
                  />
                  <span className={cn('text-sm leading-body', valueColor)}>
                    {t(option.labelKey, { defaultValue: option.defaultValue })}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
